import {
    PromotedActionsMenuEventCard,
    PromotedActionsMenuEventCardProps,
} from '@eventbrite/ads-event-card-extra';
import { FormattedEvent } from '@eventbrite/event-renderer';
import classNames from 'classnames';
import React from 'react';
import DiscoverVerticalEventCard from '../DiscoverEventCard/DiscoverVerticalEventCard';
import { SeeMoreButton } from '../SeeMoreButton';

const cityBrowseUtmOptions = {
    'utm-campaign': 'social',
    'utm-content': 'attendeeshare',
    'utm-medium': 'discovery',
    'utm-share-source': 'city-browse',
    'utm-term': '',
};

type SmallEventCardProps = Pick<
    PromotedActionsMenuEventCardProps,
    'getTrackingContext'
> & {
    cards: FormattedEvent[];
    locale: string;
    isMobile: boolean;
    isAuthenticated: boolean;
    onTrackClickInternal?: Function;
    affCode?: string;
    title: string;
    statsigLocationString?: string;
    onRemoveEvent: (eventId: string) => void;
};

const getCards = ({
    cards,
    isMobile,
    isAuthenticated,
    onTrackClickInternal,
    affCode,
    locale,
    title,
    statsigLocationString,
    getTrackingContext,
    onRemoveEvent,
}: SmallEventCardProps) => {
    const classes = classNames({
        'eds-g-cell eds-g-cell-3-12 eds-g-cell--has-overflow eds-l-pad-all-3':
            !isMobile,
        'small-card-mobile eds-l-pad-all-2': isMobile,
    });

    // This component is re-used also for event suggestions, which don't have promoted actions
    // so we need to check if the props are present
    const hasPromotedActionsProps = !!getTrackingContext && !!onRemoveEvent;

    return cards.map((event, index: number) => {
        return (
            <div className={classes} key={`${event.id}-${event.isPromoted}`}>
                <DiscoverVerticalEventCard
                    bucketLabel={title}
                    locale={locale}
                    statsigLocationString={statsigLocationString}
                    event={event}
                    isAuthenticated={isAuthenticated}
                    affCode={affCode}
                    shareOptions={{
                        utmOptions: cityBrowseUtmOptions,
                    }}
                    moreActions={
                        event.isPromoted &&
                        hasPromotedActionsProps && (
                            <PromotedActionsMenuEventCard
                                event={event}
                                onRemoveEvent={onRemoveEvent}
                                getTrackingContext={getTrackingContext}
                            />
                        )
                    }
                    onClick={() => {
                        onTrackClickInternal?.(
                            index,
                            event.id,
                            event.ticketsBy,
                        );
                    }}
                />
            </div>
        );
    });
};

type SmallEventCardGroupProps = Omit<SmallEventCardProps, 'isMobile'> & {
    linkUrl: string;
    enableClientSideLinking?: boolean;
};

export const SmallEventCardGroup = ({
    cards,
    linkUrl,
    isAuthenticated,
    title,
    onTrackClickInternal,
    affCode,
    enableClientSideLinking,
    statsigLocationString,
    getTrackingContext,
    onRemoveEvent,
    locale,
}: SmallEventCardGroupProps) => (
    <div className="browse-cards-container">
        <div className="eds-show-up-md">
            {getCards({
                cards,
                locale,
                isMobile: false,
                isAuthenticated,
                onTrackClickInternal,
                affCode,
                title,
                statsigLocationString,
                getTrackingContext,
                onRemoveEvent,
            })}
        </div>
        <div className="eds-show-down-mn">
            <div className="mobile-card-carousel mobile-card-carousel__city-browse">
                {getCards({
                    cards,
                    isMobile: true,
                    isAuthenticated,
                    onTrackClickInternal,
                    affCode,
                    locale,
                    title,
                    getTrackingContext,
                    onRemoveEvent,
                })}
            </div>
            <div className="eds-show-down-sn eds-align--center">
                <div className="eds-g-cell">
                    {linkUrl && (
                        <SeeMoreButton
                            linkUrl={linkUrl}
                            title={title}
                            enableClientSideLinking={enableClientSideLinking}
                        />
                    )}
                </div>
            </div>
        </div>
    </div>
);

export default SmallEventCardGroup;
